import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";

// Import background and icons
import bgImg from "../../images/bg/industries-bg02.png";
import icon01 from "../../images/icon/service-icon01.svg";
import icon02 from "../../images/icon/service-icon02.svg";
import icon03 from "../../images/icon/service-icon03.svg";
import icon04 from "../../images/icon/service-icon04.svg";
import icon05 from "../../images/icon/service-icon05.svg";
import icon06 from "../../images/icon/service-icon06.svg";
import icon07 from "../../images/icon/service-icon07.svg";

// Static data - 19 Industry Verticals (matches signup INDUSTRY_OPTIONS)
//
// Entries with a `path` are NOT verticals — they are solution pages for a
// distinct buyer inside an existing vertical, so they get a card without
// changing the vertical count. PG / hostel / co-living is a property type in
// the hotel vertical; there is no `pg` key in mothership VERTICAL_MODULES.
const FULL_MODULES = [
  { slug: 'hotel', icon: icon01, titleKey: 'industriesData.hotel.title', descKey: 'industriesData.hotel.desc', storefront: true },
  { slug: 'pg-hostel', path: '/industry/pg-hostel-management-software', icon: icon01, titleKey: 'industriesData.pgHostel.title', descKey: 'industriesData.pgHostel.desc', storefront: true },
  { slug: 'restaurant', icon: icon02, titleKey: 'industriesData.restaurant.title', descKey: 'industriesData.restaurant.desc', storefront: true },
  { slug: 'travel', icon: icon06, titleKey: 'industriesData.travel.title', descKey: 'industriesData.travel.desc', storefront: true, voyageManaged: true },
  { slug: 'healthcare', icon: icon04, titleKey: 'industriesData.healthcare.title', descKey: 'industriesData.healthcare.desc', storefront: true },
  { slug: 'education', icon: icon03, titleKey: 'industriesData.education.title', descKey: 'industriesData.education.desc', storefront: true },
  { slug: 'logistics', icon: icon05, titleKey: 'industriesData.logistics.title', descKey: 'industriesData.logistics.desc', storefront: true },
  { slug: 'real-estate', icon: icon07, titleKey: 'industriesData.realEstate.title', descKey: 'industriesData.realEstate.desc', storefront: true },
  { slug: 'retail', icon: icon01, titleKey: 'industriesData.retail.title', descKey: 'industriesData.retail.desc', storefront: true },
  { slug: 'manufacturing', icon: icon05, titleKey: 'industriesData.manufacturing.title', descKey: 'industriesData.manufacturing.desc' },
  { slug: 'construction', icon: icon03, titleKey: 'industriesData.construction.title', descKey: 'industriesData.construction.desc' },
  { slug: 'consulting', icon: icon07, titleKey: 'industriesData.consulting.title', descKey: 'industriesData.consulting.desc' },
  { slug: 'it-services', icon: icon05, titleKey: 'industriesData.itServices.title', descKey: 'industriesData.itServices.desc', isNew: true },
  { slug: 'ecommerce', icon: icon06, titleKey: 'industriesData.ecommerce.title', descKey: 'industriesData.ecommerce.desc', storefront: true },
  { slug: 'car-dealer', icon: icon02, titleKey: 'industriesData.carDealer.title', descKey: 'industriesData.carDealer.desc', storefront: true, isNew: true },
  { slug: 'bike-dealer', icon: icon03, titleKey: 'industriesData.bikeDealer.title', descKey: 'industriesData.bikeDealer.desc', storefront: true, isNew: true },
  { slug: 'salon-spa', icon: icon04, titleKey: 'industriesData.salonSpa.title', descKey: 'industriesData.salonSpa.desc', storefront: true, isNew: true },
  { slug: 'gym-fitness', icon: icon05, titleKey: 'industriesData.gym.title', descKey: 'industriesData.gym.desc', storefront: true, isNew: true },
  { slug: 'arena-gaming', icon: icon06, titleKey: 'industriesData.arena.title', descKey: 'industriesData.arena.desc', storefront: true, isNew: true },
  { slug: 'study-abroad', icon: icon03, titleKey: 'industriesData.studyAbroad.title', descKey: 'industriesData.studyAbroad.desc', storefront: true, isNew: true },
];

// AI Costing compatible industries
const AI_COSTING_INDUSTRIES = [
  { icon: icon02, titleKey: 'industriesData.electrical.title', descKey: 'industriesData.electrical.desc' },
  { icon: icon03, titleKey: 'industriesData.construction.title', descKey: 'industriesData.construction.desc' },
  { icon: icon04, titleKey: 'industriesData.hvac.title', descKey: 'industriesData.hvac.desc' },
  { icon: icon05, titleKey: 'industriesData.manufacturing.title', descKey: 'industriesData.manufacturing.desc' },
  { icon: icon06, titleKey: 'industriesData.itServices.title', descKey: 'industriesData.itServices.desc' },
  { icon: icon07, titleKey: 'industriesData.retail.title', descKey: 'industriesData.retail.desc' },
  { icon: icon01, titleKey: 'industriesData.professional.title', descKey: 'industriesData.professional.desc' },
];

// Custom CSS for Industries - uses theme variables
const industriesStyles = `
  .industries-module-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 24px;
    margin-bottom: 60px;
  }
  @media (max-width: 1199px) {
    .industries-module-grid {
      grid-template-columns: repeat(3, 1fr);
    }
  }
  @media (max-width: 991px) {
    .industries-module-grid {
      grid-template-columns: repeat(2, 1fr);
    }
  }
  @media (max-width: 575px) {
    .industries-module-grid {
      grid-template-columns: 1fr;
    }
  }
  .industries-module-card {
    background: rgba(15, 20, 40, 0.6);
    border: 1px solid rgba(0, 255, 151, 0.2);
    border-radius: 16px;
    padding: 24px;
    transition: all 0.3s ease;
    position: relative;
    display: flex;
    flex-direction: column;
    height: 100%;
  }
  .industries-module-card:hover {
    border-color: var(--color-primary);
    transform: translateY(-4px);
    box-shadow: 0 10px 40px rgba(0, 255, 151, 0.1);
  }
  .industries-module-badge {
    position: absolute;
    top: 12px;
    right: 12px;
    background: linear-gradient(135deg, var(--color-primary), #00cc79);
    color: var(--color-secondary);
    font-size: 9px;
    font-weight: 700;
    padding: 4px 10px;
    border-radius: 12px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  .industries-module-icon {
    width: 60px;
    height: 60px;
    background: linear-gradient(135deg, rgba(0, 255, 151, 0.15), rgba(0, 255, 151, 0.05));
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 20px;
  }
  .industries-module-icon img {
    width: 32px;
    height: 32px;
    filter: brightness(0) saturate(100%) invert(87%) sepia(33%) saturate(1352%) hue-rotate(88deg) brightness(104%) contrast(104%);
  }
  .industries-module-title {
    font-size: 1.1rem;
    font-weight: 600;
    margin-bottom: 10px;
    color: var(--color-white);
    min-height: 28px;
  }
  .industries-module-desc {
    font-size: 0.85rem;
    color: rgba(255, 255, 255, 0.7);
    line-height: 1.5;
    margin-bottom: 14px;
    flex: 1;
  }
  .industries-card-badges {
    margin-top: auto;
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }
  .industries-storefront-badge {
    display: inline-block;
    background: rgba(0, 255, 151, 0.1);
    color: var(--color-primary);
    font-size: 10px;
    font-weight: 500;
    padding: 4px 8px;
    border-radius: 6px;
  }
  .industries-voyage-badge {
    display: inline-block;
    background: rgba(138, 43, 226, 0.15);
    color: #a78bfa;
    font-size: 10px;
    font-weight: 500;
    padding: 4px 8px;
    border-radius: 6px;
  }
  .industries-section-title {
    text-align: center;
    margin-bottom: 30px;
    color: var(--color-primary);
    font-size: 1.5rem;
    font-weight: 600;
  }
  .industries-section-subtitle {
    display: block;
    font-size: 0.9rem;
    color: var(--color-gray);
    font-weight: 400;
    margin-top: 8px;
  }
  .industries-costing-title {
    text-align: center;
    margin-bottom: 30px;
    color: #00d4a1;
    font-size: 1.3rem;
    font-weight: 600;
  }
  .xb-industries-inner {
    display: flex !important;
    align-items: stretch !important;
  }
  .xb-industries-item {
    width: 259px !important;
    min-height: 290px !important;
    height: 290px !important;
    display: inline-flex !important;
    flex-direction: column !important;
    align-items: center !important;
    justify-content: flex-start !important;
    padding: 30px 15px 25px !important;
    box-sizing: border-box !important;
    vertical-align: top;
  }
  .xb-industries-item .xb-icon {
    margin-bottom: 20px !important;
    flex-shrink: 0;
  }
  .xb-industries-item .xb-title {
    font-size: 19px !important;
    line-height: 1.35 !important;
    min-height: 52px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    text-align: center !important;
    margin: 0 !important;
    padding: 0 4px;
    width: 100%;
    word-break: break-word;
    flex: 1;
  }
  .xb-industries-item .ai-costing-badge {
    position: absolute;
    top: 10px;
    right: 10px;
    background: linear-gradient(135deg, #00a4af, #00d4a1);
    color: var(--color-secondary);
    font-size: 8px;
    font-weight: 600;
    padding: 3px 6px;
    border-radius: 10px;
    text-transform: uppercase;
    z-index: 2;
  }
  @media (max-width: 767px) {
    .xb-industries-item {
      width: 190px !important;
      min-height: 230px !important;
      height: 230px !important;
      padding: 20px 10px 20px !important;
    }
    .xb-industries-item .xb-icon {
      width: 90px !important;
      height: 90px !important;
      margin-bottom: 15px !important;
    }
    .xb-industries-item .xb-title {
      font-size: 16px !important;
      min-height: 44px !important;
    }
  }
`;

interface IndustriesSectionProps {
  limit?: number;
  showViewAll?: boolean;
}

const IndustriesSection: React.FC<IndustriesSectionProps> = ({ limit, showViewAll }) => {
  const { t } = useTranslation();
  const modulesToShow = limit ? FULL_MODULES.slice(0, limit) : FULL_MODULES;

  useEffect(() => {
    const section = document.querySelector<HTMLElement>(".industries.bg-wrap");
    if (section) {
      section.style.backgroundImage = `url(${bgImg})`;
    }
  }, []);

  return (
    <section className="industries pb-100 bg_img bg-wrap">
      <style>{industriesStyles}</style>
      <div className="container">
        {/* Section Header */}
        <div className="sec-title sec-title-center text-center mb-50">
          <span className="sub-title mb-15">{t('industries.subtitle')}</span>
          <h2 className="title">{t('industries.title')}</h2>
          <p className="mt-20 opacity-75">{t('industries.description')}</p>
        </div>

        {/* 19 Industry Verticals - Grid */}
        <div className="mb-60">
          <h3 className="industries-section-title">
            {t('industries.fullModulesTitle')}
            <span className="industries-section-subtitle">
              {t('industries.fullModulesSubtitle')}
            </span>
          </h3>

          <div className="industries-module-grid">
            {modulesToShow.map((item, index) => (
              <Link to={(item as { path?: string }).path || `/industry/${item.slug}`} className="industries-module-card" key={index} style={{ textDecoration: 'none', color: 'inherit' }}>
                <span className="industries-module-badge">
                  {t('industries.fullModuleBadge')}
                </span>
                {item.isNew && (
                  <span className="industries-module-badge" style={{ background: 'linear-gradient(135deg, #ff6b6b, #ee5253)', right: 'auto', left: 12 }}>
                    NEW
                  </span>
                )}

                <div className="industries-module-icon">
                  <img src={item.icon} alt={t(item.titleKey)} />
                </div>

                <h4 className="industries-module-title">
                  {t(item.titleKey)}
                </h4>

                <p className="industries-module-desc">
                  {t(item.descKey)}
                </p>

                <div className="industries-card-badges">
                  {item.storefront && (
                    <span className="industries-storefront-badge">
                      {t('industries.storefrontBadge')}
                    </span>
                  )}
                  {item.voyageManaged && (
                    <span className="industries-voyage-badge">
                      VOYAGE AI
                    </span>
                  )}
                </div>
              </Link>
            ))}
          </div>

          {showViewAll && (
            <div className="text-center mt-20">
              <Link to="/industries" className="thm-btn agency-btn" style={{ display: 'inline-block' }}>
                <span className="text">View All 19 Industries</span>
              </Link>
            </div>
          )}
        </div>

        {/* AI Costing Industries - Marquee */}
        <div>
          <h3 className="industries-costing-title">
            {t('industries.aiCostingTitle')}
            <span className="industries-section-subtitle">
              {t('industries.aiCostingSubtitle')}
            </span>
          </h3>
        </div>
      </div>

      {/* Marquee for AI Costing Industries */}
      <div className="xb-industries-wrapper d-inline-block">
        <Marquee
          direction="right"
          speed={40}
          gradient={false}
          pauseOnHover={true}
          className="marquee-right"
        >
          <div className="xb-industries-inner ul_li">
            {AI_COSTING_INDUSTRIES.map((item, index) => (
              <div className="xb-industries-item xb-border" key={index}>
                <span className="ai-costing-badge">
                  {t('industries.aiCostingBadge')}
                </span>
                <div className="xb-icon">
                  <img src={item.icon} alt={t(item.titleKey)} />
                </div>
                <h3 className="xb-title">{t(item.titleKey)}</h3>
              </div>
            ))}
          </div>
        </Marquee>
      </div>
    </section>
  );
};

export default IndustriesSection;
